/**
 * AppModal — Reusable modal via React Portal
 *
 * By mounting on document.body, this component is completely outside
 * the layout's stacking context, so it always appears above the navbar,
 * sidebar, and any fixed/sticky elements regardless of their z-index.
 *
 * Usage:
 *   <AppModal open={show} onClose={() => setShow(false)} title="Título" size="lg">
 *     ...content...
 *     <AppModal.Footer>
 *       <button onClick={onClose}>Cancelar</button>
 *       <button>Guardar</button>
 *     </AppModal.Footer>
 *   </AppModal>
 *
 * Sizes: sm (480px) | md (640px) | lg (860px) | xl (1080px)
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './AppModal.css';

function AppModal({ open, onClose, title, subtitle, size = 'md', children, noCloseOnBackdrop = false }) {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="am-overlay"
      onClick={noCloseOnBackdrop ? undefined : onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className={`am-dialog am-dialog--${size}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="am-header">
          <div className="am-header-text">
            <h5 className="am-title">{title}</h5>
            {subtitle && <p className="am-subtitle">{subtitle}</p>}
          </div>
          <button className="am-close" onClick={onClose} aria-label="Cerrar">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="am-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * AppModal.Footer — sticky footer for action buttons
 */
AppModal.Footer = function ModalFooter({ children }) {
  return <div className="am-footer">{children}</div>;
};

export default AppModal;
